import { createElement } from 'react';
import { downloadOrderReceipt, downloadPdfFromBase64, fetchPublicOrderReceipt } from './api';

export function downloadBlobFromBase64(base64, filename, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadJpegPagesFromBase64(pages, baseFilename) {
  if (!pages || pages.length === 0) return false;

  pages.forEach((pageBase64, index) => {
    const suffix = pages.length > 1 ? `_p${index + 1}` : '';
    downloadBlobFromBase64(pageBase64, `${baseFilename}${suffix}.jpg`, 'image/jpeg');
  });
  return true;
}

export async function downloadCreatedOrderReceipt(lastCreatedOrderReceipt, lang, toast, format = 'jpeg') {
  if (!lastCreatedOrderReceipt?.orderId) return;

  const { orderId, orderNo } = lastCreatedOrderReceipt;
  const langKey = lang.toLowerCase();
  const langLabel = lang.toUpperCase();
  const loadingId = toast.loading(`Generating ${langLabel} receipt…`);

  try {
    const result = await downloadOrderReceipt(orderId, langKey, format);
    toast.dismiss(loadingId);

    if (format === 'jpeg') {
      const jpegPages = result.jpegBase64Pages
        || (langKey === 'kn' ? result.jpegBase64PagesKn : result.jpegBase64PagesMr);
      if (jpegPages?.length) {
        downloadJpegPagesFromBase64(jpegPages, `Receipt_${orderNo}_${langLabel}`);
        toast.success(`${langLabel} receipt downloaded (JPEG)`);
        return;
      }

      const pdf = result.pdfBase64
        || (langKey === 'kn' ? result.pdfBase64Kn : result.pdfBase64Mr);
      if (pdf) {
        downloadPdfFromBase64(pdf, `Receipt_${orderNo}_${langLabel}.pdf`);
        toast.success(`${langLabel} receipt downloaded (PDF fallback)`);
        return;
      }

      toast.error(`${langLabel} receipt not available`);
      return;
    }

    const pdf = result.pdfBase64
      || (langKey === 'kn' ? result.pdfBase64Kn : result.pdfBase64Mr);
    if (pdf) {
      downloadPdfFromBase64(pdf, `Receipt_${orderNo}_${langLabel}.pdf`);
      toast.success(`${langLabel} receipt downloaded (PDF)`);
      return;
    }

    toast.error(`${langLabel} receipt not available`);
  } catch (error) {
    toast.dismiss(loadingId);
    console.error('Error downloading receipt:', error);
    toast.error(error.message || 'Failed to download receipt');
  }
}

export function showShopOrderSuccessToast(result, toast, messages) {
  const { successMessage, pdfHintMessage, downloadPdfLabel } = messages;

  downloadShopOrderReceipt(result, 'jpeg');

  toast.custom(
    (toastInstance) => createElement(
      'div',
      { className: 'flex flex-col gap-2 text-sm' },
      createElement('p', null, successMessage),
      result.order?._id && createElement(
        'button',
        {
          type: 'button',
          className: 'min-h-11 px-2 inline-flex items-center font-semibold underline text-left',
          onClick: () => {
            downloadShopOrderReceipt(result, 'pdf');
            toast.dismiss(toastInstance.id);
          },
        },
        `${pdfHintMessage} ${downloadPdfLabel}`
      )
    ),
    { duration: 8000 }
  );
}

export async function downloadShopOrderReceipt(result, format = 'jpeg') {
  const orderId = result?.order?._id;
  const orderNo = result?.order?.orderNo;
  if (!orderNo) return false;

  if (format === 'jpeg' && result.jpegBase64Pages?.length) {
    downloadJpegPagesFromBase64(result.jpegBase64Pages, `Receipt_${orderNo}`);
    return true;
  }

  if (format === 'pdf' && result.pdfBase64) {
    downloadPdfFromBase64(result.pdfBase64, `Receipt_${orderNo}.pdf`);
    return true;
  }

  if (format === 'jpeg' && result.pdfBase64) {
    downloadPdfFromBase64(result.pdfBase64, `Receipt_${orderNo}.pdf`);
    return true;
  }

  if (!orderId) return false;

  const lang = result.order.receiptLocale || 'mr';
  const data = await fetchPublicOrderReceipt(orderId, lang, format);

  if (format === 'jpeg') {
    if (data.jpegBase64Pages?.length) {
      downloadJpegPagesFromBase64(data.jpegBase64Pages, `Receipt_${orderNo}`);
      return true;
    }
    if (data.pdfBase64) {
      downloadPdfFromBase64(data.pdfBase64, `Receipt_${orderNo}.pdf`);
      return true;
    }
    return false;
  }

  if (data.pdfBase64) {
    downloadPdfFromBase64(data.pdfBase64, `Receipt_${orderNo}.pdf`);
    return true;
  }

  return false;
}
